/**
 * NRC EWAS API Client
 * Handles connection to NRC EWAS data pipeline
 */

import type {
  EWASDataRecord,
  EWASSourcesResponse,
  EWASDataResponse,
  EWASStatsResponse,
  EWASQueryFilters,
  EWASSourceId,
} from './types';

// Environment configuration
const EWAS_API_BASE_URL = process.env.REACT_APP_EWAS_API_URL || 'https://nrc-ewas.masae-analytics.com';

// Simulated delay for mock API responses
const MOCK_DELAY = 300;

class EWASClient {
  private baseUrl: string;
  private useMockData: boolean;

  constructor(baseUrl: string = EWAS_API_BASE_URL) {
    this.baseUrl = baseUrl;
    // Use mock data if no real API endpoint is configured or in development
    this.useMockData = !process.env.REACT_APP_EWAS_API_URL || process.env.NODE_ENV === 'development';
  }

  /**
   * Helper to delay responses (simulates network latency)
   */
  private async delay(ms: number = MOCK_DELAY): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms + Math.random() * 100));
  }

  /**
   * Fetch available data sources
   */
  async getSources(): Promise<EWASSourcesResponse> {
    if (this.useMockData) {
      await this.delay();
      const { mockEWASSources } = await import('../../data/ewasData');
      return {
        sources: mockEWASSources,
        lastUpdated: new Date().toISOString(),
      };
    }

    const response = await fetch(`${this.baseUrl}/pipeline/api/sources/`);
    if (!response.ok) {
      throw new Error(`EWAS API error: ${response.statusText}`);
    }
    return response.json();
  }

  /**
   * Fetch data records with optional filters
   */
  async getData<T extends EWASDataRecord = EWASDataRecord>(
    filters?: EWASQueryFilters
  ): Promise<EWASDataResponse<T>> {
    if (this.useMockData) {
      await this.delay();
      const { getMockEWASRecords } = await import('../../data/ewasData');
      const allRecords = getMockEWASRecords(filters);
      const offset = filters?.offset || 0;
      const limit = filters?.limit || 50;

      return {
        data: allRecords.slice(offset, offset + limit) as T[],
        totalCount: allRecords.length,
        page: Math.floor(offset / limit) + 1,
        pageSize: limit,
        hasMore: offset + limit < allRecords.length,
      };
    }

    const params = new URLSearchParams();
    if (filters?.source) {
      const sources = Array.isArray(filters.source) ? filters.source : [filters.source];
      sources.forEach(s => params.append('source', s));
    }
    if (filters?.severity) {
      const severities = Array.isArray(filters.severity) ? filters.severity : [filters.severity];
      severities.forEach(s => params.append('severity', s));
    }
    if (filters?.region) params.set('region', filters.region);
    if (filters?.admin1) params.set('admin1', filters.admin1);
    if (filters?.dateFrom) params.set('date_from', filters.dateFrom);
    if (filters?.dateTo) params.set('date_to', filters.dateTo);
    if (filters?.limit) params.set('limit', String(filters.limit));
    if (filters?.offset) params.set('offset', String(filters.offset));

    const response = await fetch(`${this.baseUrl}/pipeline/api/data/?${params}`);
    if (!response.ok) {
      throw new Error(`EWAS API error: ${response.statusText}`);
    }
    return response.json();
  }

  /**
   * Fetch aggregated statistics
   */
  async getStatistics(): Promise<EWASStatsResponse> {
    if (this.useMockData) {
      await this.delay();
      const { mockEWASStatistics } = await import('../../data/ewasData');
      return {
        statistics: mockEWASStatistics,
        generatedAt: new Date().toISOString(),
      };
    }

    const response = await fetch(`${this.baseUrl}/pipeline/api/statistics/`);
    if (!response.ok) {
      throw new Error(`EWAS API error: ${response.statusText}`);
    }
    return response.json();
  }

  /**
   * Get source-specific data
   */
  async getSourceData(
    sourceId: EWASSourceId,
    filters?: Omit<EWASQueryFilters, 'source'>
  ): Promise<EWASDataResponse> {
    return this.getData({ ...filters, source: sourceId });
  }

  /**
   * Get ACLED conflict events
   */
  async getACLEDEvents(filters?: Omit<EWASQueryFilters, 'source'>) {
    const { getACLEDMockData } = await import('../../data/ewasData');
    if (this.useMockData) {
      await this.delay();
      return getACLEDMockData(filters);
    }
    return this.getSourceData('acled', filters);
  }

  /**
   * Get IOM DTM displacement data
   */
  async getIOMDisplacement(filters?: Omit<EWASQueryFilters, 'source'>) {
    const { getIOMDTMMockData } = await import('../../data/ewasData');
    if (this.useMockData) {
      await this.delay();
      return getIOMDTMMockData(filters);
    }
    return this.getSourceData('iom_dtm', filters);
  }

  /**
   * Get FEWSNET food security data
   */
  async getFEWSNETData(filters?: Omit<EWASQueryFilters, 'source'>) {
    const { getFEWSNETMockData } = await import('../../data/ewasData');
    if (this.useMockData) {
      await this.delay();
      return getFEWSNETMockData(filters);
    }
    return this.getSourceData('fewsnet', filters);
  }

  /**
   * Get Dataminr social media alerts
   */
  async getDataminrAlerts(filters?: Omit<EWASQueryFilters, 'source'>) {
    const { getDataminrMockData } = await import('../../data/ewasData');
    if (this.useMockData) {
      await this.delay();
      return getDataminrMockData(filters);
    }
    return this.getSourceData('dataminr', filters);
  }

  /**
   * Check API health
   */
  async healthCheck(): Promise<{ status: 'healthy' | 'degraded' | 'offline'; message: string }> {
    if (this.useMockData) {
      return { status: 'healthy', message: 'Using mock data' };
    }

    try {
      const response = await fetch(`${this.baseUrl}/health/`, { method: 'HEAD' });
      if (response.ok) {
        return { status: 'healthy', message: 'API is responding' };
      }
      return { status: 'degraded', message: 'API returned non-200 status' };
    } catch (error) {
      return { status: 'offline', message: 'Cannot reach API' };
    }
  }
}

// Export singleton instance
export const ewasClient = new EWASClient();

// Export class for testing/custom instances
export { EWASClient };
